import type { LucideIcon } from "lucide-react";
import {
  BarChart3,
  CalendarClock,
  CreditCard,
  FileCheck2,
  Globe,
  Radio,
  Rocket,
  Smartphone,
  Sparkles,
  Users,
} from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { LinkButton } from "@/components/ui/LinkButton";

/**
 * Which audience the merged value section is showing. A single page-level
 * audience state (lifted into HomePage) drives this section, the pricing
 * section, and the FAQ together, so the whole value area speaks to one reader
 * at a time instead of stacking two parallel pitches.
 */
export type Audience = "companies" | "drivers";

/**
 * One value prop in the audience grid. `tier` is an optional plan marker shown
 * as a badge (e.g. the Business Ultimate AI auto-dispatch capability).
 */
interface ValueProp {
  icon: LucideIcon;
  title: string;
  description: string;
  /** Plan the capability unlocks on, e.g. "Business Ultimate". */
  tier?: string;
}

/** The copy + value props rendered for one audience. */
interface AudienceContent {
  /** Eyebrow chip label. */
  eyebrow: string;
  heading: string;
  /** Cobalt-accented second line of the heading. */
  headingAccent: string;
  subhead: string;
  props: readonly ValueProp[];
  /** CTA destination — companies and drivers register through distinct flows. */
  ctaHref: string;
  ctaLabel: string;
}

/**
 * Audience-specific value content. Every claim is grounded in the platform docs
 * (docs/subscriptions.md, docs/features.md, docs/driver-management.md) — no
 * fabricated metrics, prices, or customer names. The AI auto-dispatch entry is
 * the Business Ultimate capability and carries its tier badge.
 */
const CONTENT: Record<Audience, AudienceContent> = {
  companies: {
    eyebrow: "For operators & fleets",
    heading: "Run the whole operation",
    headingAccent: "from one console",
    subhead:
      "From your first hire to a full fleet, RideReserv handles dispatch, drivers, bookings, and payments — so your team coordinates more trips with less back-and-forth.",
    props: [
      {
        icon: Sparkles,
        title: "AI auto-dispatch",
        description:
          "Let the platform propose the right driver for every reservation — read against your rules for zones, vehicle types, ratings, and rest gaps.",
        tier: "Business Ultimate",
      },
      {
        icon: Radio,
        title: "Dispatch & assignment",
        description:
          "Assign every reservation from one console, with the whole team's schedule in view.",
      },
      {
        icon: Users,
        title: "Driver management",
        description:
          "Invite drivers, collect their documents, and run optional approval workflows before they take a trip.",
      },
      {
        icon: Globe,
        title: "Branded booking",
        description:
          "Take reservations on a hosted page in your colors, or embed the booking widget on your own site.",
      },
      {
        icon: CreditCard,
        title: "Online payments",
        description:
          "Collect card payments through Stripe Connect — funds settle straight to your account.",
      },
      {
        icon: BarChart3,
        title: "Analytics dashboard",
        description:
          "Track revenue, reservation trends, cancellation rate, and driver utilization across your operation.",
      },
    ],
    ctaHref: "/register?type=business",
    ctaLabel: "Get started",
  },
  drivers: {
    eyebrow: "For independent drivers",
    heading: "Run your own driving business",
    headingAccent: "not someone else's app",
    subhead:
      "You're the operator here. Bring your documents, your customers, and your schedule, and keep what you earn — on the same operations console fleets run on.",
    props: [
      {
        icon: FileCheck2,
        title: "Your documents travel with you",
        description:
          "Upload your license, insurance, and credentials once, then share them with any operator you work with. Revoke access whenever you want.",
      },
      {
        icon: Globe,
        title: "Your own branded booking page",
        description:
          "Get a public booking link customers use directly — your name, your brand, your bookings. No marketplace fees.",
      },
      {
        icon: CalendarClock,
        title: "Reservations & availability",
        description:
          "Set your availability, confirm trips, and track every reservation from a single operations view.",
      },
      {
        icon: CreditCard,
        title: "Get paid your way",
        description:
          "Take card payments online through Stripe on the paid plan, or track cash and transfers by hand — status stays in sync either way.",
      },
      {
        icon: Smartphone,
        title: "Built for the road",
        description:
          "A fast, mobile-first interface designed for touch — run your business from the driver's seat, not a desk.",
      },
      {
        icon: Rocket,
        title: "Start free, upgrade as you grow",
        description:
          "Launch on the free plan with no card required. Move up for online payments when you're ready.",
      },
    ],
    ctaHref: "/register?type=driver",
    ctaLabel: "Start driving",
  },
};

interface AudienceValueSectionProps {
  /** The audience to render — controlled by the page-level toggle. */
  audience: Audience;
}

/**
 * Merged homepage value section — replaces the old back-to-back CompaniesSection
 * and DriversSection with ONE section whose content is driven by the page-level
 * audience toggle. Showing a single audience at a time keeps the funnel focused
 * (one reader, one pitch, one primary CTA) instead of asking every visitor to
 * scroll past the half that isn't theirs.
 *
 * Operations-console styling: an asymmetric header (pitch left, CTA right at
 * lg) over a dense, hairline-bordered "spec sheet" grid of value props. Purely
 * presentational; flip-safe for dark mode; token classes only. The toggle that
 * controls `audience` lives in the page shell (HomePage), not here.
 * @param root0 - Component props
 * @param root0.audience - The audience to render
 * @returns The merged audience value section
 */
export function AudienceValueSection({ audience }: AudienceValueSectionProps) {
  const content = CONTENT[audience];

  return (
    <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
      {/* Header: asymmetric — pitch on the left, CTA on the right at lg. */}
      <div className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between">
        <div className="max-w-2xl">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-surface px-3 py-1 font-mono text-[11px] font-medium uppercase tracking-[0.18em] text-neutral-600">
            {/* Accent dot — `bg-primary-500` re-themes via the page-level
                audience scope (cobalt for operators, amber for drivers). */}
            <span
              aria-hidden="true"
              className="size-1.5 rounded-full bg-primary-500 motion-safe:animate-pulse"
            />
            {content.eyebrow}
          </span>
          <h2 className="mt-5 text-3xl font-bold tracking-tight text-neutral-900 sm:text-4xl">
            {content.heading}
            <br />
            {/* primary-600 has no dark flip and is too low-contrast on a dark
                page; brighten to primary-400 in dark (per design-system docs). */}
            <span className="text-primary-600 dark:text-primary-400">
              {content.headingAccent}
            </span>
          </h2>
          <p className="mt-4 text-lg text-neutral-600">{content.subhead}</p>
        </div>

        {/* Desktop CTA — the section's single primary action, inline with the
            header at lg. The mobile CTA lives below the grid. The reassurance
            line is audience-specific: drivers have a real self-serve free tier
            (no card); companies onboard through a guided, sales-assisted flow
            with no free trial, so they get honest guided framing instead. */}
        <div className="hidden shrink-0 flex-col items-start gap-2 lg:flex">
          <LinkButton to={content.ctaHref} size="lg">
            {content.ctaLabel}
          </LinkButton>
          <span className="font-mono text-xs text-neutral-500">
            {audience === "drivers"
              ? "Free to start · no card required"
              : "We’ll help you get set up"}
          </span>
        </div>
      </div>

      {/* Spec-sheet grid: one hairline panel, gap-px dividers (survives wrap).
          bg-neutral-200 is the divider color; each cell paints bg-surface. */}
      <ul className="mt-10 grid grid-cols-1 gap-px overflow-hidden rounded-md border border-neutral-200 bg-neutral-200 sm:grid-cols-2 lg:grid-cols-3">
        {content.props.map(({ icon: Icon, title, description, tier }, index) => (
          <li key={title} className="flex flex-col bg-surface p-5">
            <div className="flex items-center justify-between gap-3">
              <span
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-sm border border-neutral-200 text-primary-600 dark:text-primary-400"
                aria-hidden="true"
              >
                <Icon className="h-4 w-4" strokeWidth={1.75} />
              </span>
              {tier ? (
                <Badge variant="secondary" className="shrink-0 uppercase tracking-wide">
                  {tier}
                </Badge>
              ) : (
                <span className="font-mono text-xs tabular-nums text-neutral-500">
                  {String(index + 1).padStart(2, "0")}
                </span>
              )}
            </div>
            <h3 className="mt-4 text-base font-semibold text-neutral-900">
              {title}
            </h3>
            <p className="mt-1.5 text-sm leading-relaxed text-neutral-600">
              {description}
            </p>
          </li>
        ))}
      </ul>

      {/* Mobile CTA — the desktop cluster is hidden below lg. The reassurance
          row mirrors the desktop one: the "Start free / no card" badge is
          driver-only (real self-serve free tier); companies get guided framing
          because their onboarding is sales-assisted with no free trial. */}
      <div className="mt-8 flex flex-col items-start gap-3 lg:hidden">
        <LinkButton to={content.ctaHref} size="lg" className="w-full sm:w-auto">
          {content.ctaLabel}
        </LinkButton>
        {audience === "drivers" ? (
          <div className="flex items-center gap-2">
            <Badge variant="success">Start free</Badge>
            <span className="font-mono text-xs text-neutral-500">
              no card required
            </span>
          </div>
        ) : (
          <span className="font-mono text-xs text-neutral-500">
            We’ll help you get set up
          </span>
        )}
      </div>
    </div>
  );
}
